import {gql} from '@apollo/client';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFieldOptions} from 'registry/field/FieldTableOptionGetters';
import {PlateSchemaType, StorableSchema} from 'samples/storables/StorableSchemaConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaInterface} from 'schemas/SchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {assertNever} from 'util/Types';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';
import {StorableFieldTableRowContainerGridsQuery} from './__generated/StorableFieldTableRowContainerGridsQuery';
import FieldTableRow, {DisablableControl} from './FieldTableRow';

export interface StorableFieldTableRowProps {
  schemaFieldSelectors: Array<SchemaFieldSelector>;
  ownerUrlId: string;
  registryId: string;
  id: string;
  initialField: SchemaField;
  onCancel: (id: string) => void;
  onEdit: (id: string) => void;
  requestChange: (id: string, fieldWithChanges: SchemaField) => void;
  requestRemove: (id: string) => void;
  tagSchemas?: Array<TagSchemaSummaryGraphQL>;
  storableSchemas?: Array<StorableSchema>;
  schemaInterfaces: Array<SchemaInterface>;
  shouldShowDescription?: boolean;
  shouldShowIsRequired: boolean;
  schemaId: string | null;
  schemaType: string;
  isBeingEdited: boolean;
}

/** @package exportd for testing */
export const STORABLE_FIELD_TABLE_ROW_CONTAINER_GRIDS_QUERY = gql`
  query StorableFieldTableRowContainerGridsQuery($id: StrictString!)
  @engComponent(name: INVENTORY)
  @skipAudit {
    storableSchema(id: $id) {
      ... on ContainerSchemaNode {
        id
        gridSchemas {
          ... on PlateSchemaNode {
            id
            plateSchemaType
          }
        }
      }
    }
  }
`;

/** Indicates if "isRequired" is supported for the given schema. */
enum IsRequiredSupport {
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  PENDING_FIXED_PLATE_QUERY = 'PENDING_FIXED_PLATE_QUERY',
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
  DISABLED_FOR_FIXED_PLATE_CONTAINER = 'DISABLED_FOR_FIXED_PLATE_CONTAINER',
}

/**
 * Return required fields availability for the given schema, or if a query is needed to make that determination.
 */
function getPrequeryIsRequiredSupport(
  schemaType: string,
  schemaId: string | null,
  isBeingEdited: boolean
):
  | {support: IsRequiredSupport.NOT_APPLICABLE | IsRequiredSupport.ENABLED | IsRequiredSupport.DISABLED}
  | {support: IsRequiredSupport.PENDING_FIXED_PLATE_QUERY; variables: {id: string}} {
  // If a field is not actively being edited, we don't need to determine if required fields are disabled.
  if (!isBeingEdited) {
    return {support: IsRequiredSupport.NOT_APPLICABLE};
  }
  // If the schema is not a container schema, a config flag readily tells us if required fields are enabled.
  if (schemaType !== ModelTypeConstants.CONTAINER) {
    return {
      support: BenchlingConfig.INVENTORY_ENABLE_REQUIRED_FIELDS_FOR_NON_CONTAINER_SCHEMAS
        ? IsRequiredSupport.ENABLED
        : IsRequiredSupport.DISABLED,
    };
  }
  // For a container...
  if (BenchlingConfig.INVENTORY_ENABLE_REQUIRED_FIELDS_FOR_CONTAINER_SCHEMAS) {
    // ... one config flag tells us if required fields are enabled unconditionally.
    return {support: IsRequiredSupport.ENABLED};
  } else if (BenchlingConfig.INVENTORY_ENABLE_REQUIRED_FIELDS_FOR_NON_FIXED_PLATE_CONTAINER_SCHEMAS) {
    // ... the other, indicates we need to check if the schema is associated with a fixed plate schema.
    return schemaId != null
      ? {support: IsRequiredSupport.PENDING_FIXED_PLATE_QUERY, variables: {id: schemaId}}
      : // A non-existent schema means we are dealing with a new container schema, which obviously is not
        // associated with a fixed plate.
        {support: IsRequiredSupport.ENABLED};
  } else {
    return {support: IsRequiredSupport.DISABLED};
  }
}

function isFixedPlateContainerSchema(
  storableSchema: StorableFieldTableRowContainerGridsQuery['storableSchema']
): boolean {
  return (
    storableSchema.__typename === 'ContainerSchemaNode' &&
    storableSchema.gridSchemas.some(
      (grid) => grid.__typename === 'PlateSchemaNode' && grid.plateSchemaType === PlateSchemaType.FIXED_PLATE
    )
  );
}

/** Hook to determine if required fields are enabled for the given schema. */
function useIsRequiredSupport(
  schemaType: string,
  schemaId: string | null,
  isBeingEdited: boolean
): IsRequiredSupport {
  const prequeryResult = getPrequeryIsRequiredSupport(schemaType, schemaId, isBeingEdited);
  const queryResult = useSkippableBenchlingQuery<StorableFieldTableRowContainerGridsQuery>(
    STORABLE_FIELD_TABLE_ROW_CONTAINER_GRIDS_QUERY,
    prequeryResult.support !== IsRequiredSupport.PENDING_FIXED_PLATE_QUERY
      ? {skip: true}
      : {variables: prequeryResult.variables}
  );
  if (queryResult.type !== QueryResultType.SUCCESS) {
    return prequeryResult.support;
  }
  return isFixedPlateContainerSchema(queryResult.data.storableSchema)
    ? IsRequiredSupport.DISABLED_FOR_FIXED_PLATE_CONTAINER
    : IsRequiredSupport.ENABLED;
}

function getTooltipFromIsRequiredSupport(support: IsRequiredSupport): string | null {
  switch (support) {
    case IsRequiredSupport.NOT_APPLICABLE:
      return null;
    case IsRequiredSupport.ENABLED:
      return null;
    case IsRequiredSupport.PENDING_FIXED_PLATE_QUERY:
      return 'Checking for support...';
    case IsRequiredSupport.DISABLED:
      // In practice, we shouldn't show this since GridSchemaSettingsPage, LocationSchemaSettingsPage, etc.
      // should specify {shouldShowIsRequired: false}.
      return 'Required fields are not supported for this schema type.';
    case IsRequiredSupport.DISABLED_FOR_FIXED_PLATE_CONTAINER:
      return 'Required fields are not supported for containers associated with fixed plates.';
    default:
      assertNever(support);
      return null;
  }
}

export default function StorableFieldTableRow(props: StorableFieldTableRowProps): JSX.Element {
  const isRequiredSupport = useIsRequiredSupport(props.schemaType, props.schemaId, props.isBeingEdited);
  return (
    <FieldTableRow
      availableFieldOptions={getFieldOptions({
        initialField: props.initialField,
        schemaFieldSelectors: props.schemaFieldSelectors,
        storableSchemas: props.storableSchemas ?? [],
        tagSchemas: props.tagSchemas ?? [],
        schemaInterfaces: props.schemaInterfaces,
        includeJSONField: false,
        includeAssayLinks: false,
        includeBatchLinks: false,
        includeGenericBioentityLinks: true,
        includeBooleanField: false,
        includeSnapshotField: false,
        includeSequenceLinks: false,
        includeProteinLinks: false,
        includeArchivedSchemas: true,
      })}
      {...props}
      disableControlsWithTooltips={{
        [DisablableControl.IS_REQUIRED_CHECKBOX]: getTooltipFromIsRequiredSupport(isRequiredSupport),
      }}
      shouldShowWarehouseWarning={true}
      isUneditable={props.initialField.isUneditable}
      schemaId={null}
    />
  );
}
